import type { PlatformSubpageConfig } from '../types';
import { localImage } from './shared';

const img = (src: string, alt: string) => ({
  src: localImage(src),
  alt,
  width: 1200,
  height: 800,
});

export const apisIntegrationsConfig: PlatformSubpageConfig = {
  slug: 'apis-integrations',
  path: '/platform/apis-integrations',
  breadcrumbLabel: 'APIs & Integrations',
  hero: {
    title: 'Integrate your products with any ecosystem',
    subtitle:
      'Flexible REST APIs, WebSocket infrastructure, and SDKs to connect your products to any stack.',
    primaryCta: { label: 'Get Started', to: '/contact-us' },
  },
  heroImage: {
    src: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&q=80',
    alt: 'Developer writing integration code',
    width: 800,
    height: 520,
  },
  eduIntro: {
    title: 'What are IoT APIs and integrations?',
    description:
      'APIs and integrations let your backend, mobile apps, and third-party ecosystems read and control device state—enabling automations, analytics, and voice assistants without custom protocol work.',
  },
  advantages: {
    eyebrow: 'COMPETITIVE EDGE',
    title: 'Integrate with the tools you already use',
    items: [
      { icon: 'code', title: 'Developer-First APIs', description: 'REST and MQTT endpoints with clear docs and copy-ready samples.' },
      { icon: 'layers', title: 'Ecosystem Connectors', description: 'Hooks for Alexa, Google Home, AWS IoT, Firebase, and more.' },
      { icon: 'shield', title: 'Secure by Default', description: 'Token auth, scoped permissions, and TLS for every integration path.' },
    ],
  },
  featureShowcase: {
    eyebrow: 'INTEGRATION PATTERNS',
    title: 'Integration patterns for modern IoT stacks',
    subtitle: 'Copy-ready samples for REST and MQTT workflows.',
    items: [
      {
        title: 'REST API workflows',
        description: 'CRUD device state, users, and automation rules with straightforward HTTP endpoints.',
        image: img('/images/home/feature-cloud.jpg', 'REST API'),
        cta: { label: 'View REST docs', to: '/api-documentation' },
      },
      {
        title: 'MQTT real-time streams',
        description: 'Low-latency pub/sub for real-time integrations and event-driven backends.',
        image: img('/images/home/partner-dashboard.png', 'MQTT'),
        cta: { label: 'View MQTT docs', to: '/api-documentation' },
      },
      {
        title: 'Voice and cloud connectors',
        description: 'Alexa, Google Home, AWS IoT, and Firebase bridges for ecosystem reach.',
        image: img('/images/home/feature-firmware.jpg', 'Integrations'),
        cta: { label: 'Explore connectors', to: '/contact-us' },
      },
    ],
  },
  capabilities: {
    eyebrow: 'CAPABILITIES',
    title: 'APIs and connectors for any stack',
    items: [
      { icon: 'server', title: 'REST APIs', description: 'CRUD device state, users, and automation rules.' },
      { icon: 'cloud', title: 'MQTT APIs', description: 'Low-latency pub/sub for real-time integrations.' },
      { icon: 'zap', title: 'Webhooks', description: 'Push device events to your backend instantly.' },
      { icon: 'layers', title: 'AWS IoT', description: 'Bridge cloud workflows with AWS services.' },
      { icon: 'activity', title: 'Firebase', description: 'Sync mobile and cloud data with Firebase apps.' },
      { icon: 'smartphone', title: 'Alexa & Google Home', description: 'Voice control for your connected products.' },
    ],
  },
  ctaBanner: {
    title: 'Connect your stack with developer-ready APIs today',
    ctaLabel: 'Get Started',
    ctaTo: '/contact-us',
    imageSrc: '/images/cta/solution-cta-robot.webp',
  },
  faqs: {
    title: 'Frequently asked questions',
    items: [
      { question: 'What APIs are available?', answer: 'REST, MQTT, and WebSocket endpoints with comprehensive documentation.' },
      { question: 'Are code samples provided?', answer: 'Yes—copy-ready samples for REST and MQTT integration patterns.' },
      { question: 'Which ecosystems are supported?', answer: 'Alexa, Google Home, AWS IoT, Firebase, and custom webhooks.' },
    ],
  },
  sections: {
    eyebrow: 'ARCHITECTURE',
    title: 'Integration patterns for modern IoT stacks',
    subtitle: 'Copy-ready samples for REST and MQTT workflows.',
  },
};

export const apiIntegrationsList = [
  'AWS IoT',
  'Firebase',
  'Alexa',
  'Google Home',
  'MQTT APIs',
  'REST APIs',
  'Webhooks',
] as const;

export const apiCodeSamples = {
  rest: {
    label: 'REST API',
    code: `GET /api/v1/devices/{deviceId}/state
Authorization: Bearer <partner_token>

{
  "deviceId": "plug-a1b2",
  "online": true,
  "power": "on"
}`,
  },
  mqtt: {
    label: 'MQTT',
    code: `mqtt.subscribe('tenants/{tenantId}/devices/+/events');

mqtt.on('message', (topic, payload) => {
  const event = JSON.parse(payload.toString());
  handleDeviceEvent(event);
});`,
  },
} as const;
